/* Runtime chart
 * ---------------------------------------------------------------------------
 * Draws per-mode output curves from the product metafield runtime.data.
 * No dependencies. Safe to load on every page: does nothing without markup.
 *
 * Drawing happens in real pixels (viewBox is set from the measured element
 * size) so label sizes stay constant at every breakpoint. A ResizeObserver
 * handles both viewport changes and the first paint after the surrounding
 * collapsible opens, since the element has zero width while collapsed.
 */
(function () {
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';

  function el(name, attrs, text) {
    var node = document.createElementNS(NS, name);
    for (var k in attrs) {
      if (Object.prototype.hasOwnProperty.call(attrs, k)) {
        node.setAttribute(k, attrs[k]);
      }
    }
    if (text != null) node.textContent = text;
    return node;
  }

  function clockLabel(minutes) {
    var m = Math.round(minutes);
    return Math.floor(m / 60) + ':' + String(m % 60).padStart(2, '0');
  }

  function durationLabel(minutes) {
    var m = Math.round(minutes);
    var h = Math.floor(m / 60);
    var r = m % 60;
    if (h === 0) return r + 'm';
    return h + 'h ' + r + 'm';
  }

  function RuntimeChart(root) {
    this.root = root;
    var payload = root.querySelector('[data-rtc-data]');
    if (!payload) return;

    try {
      this.data = JSON.parse(payload.textContent);
    } catch (e) {
      return;
    }
    if (!this.data || !Array.isArray(this.data.modes) || !this.data.modes.length) return;

    this.svg = root.querySelector('[data-rtc-svg]');
    if (!this.svg) return;

    this.maxLumens = this.data.max_lumens || this.peakLumens();
    this.exponent = typeof this.data.exponent === 'number' ? this.data.exponent : 0.5;

    /* Curves may be supplied as raw lumens (default) or as already-computed
     * perceived brightness percentages. Percent data is the honest form when
     * that is what was recorded; converting it back to lumens would require
     * assuming both the peak output and the transform used to produce it. */
    this.units = this.data.curve_units === 'brightness_pct' ? 'pct' : 'lumens';
    this.hasLumens = this.units === 'lumens' || typeof this.data.max_lumens === 'number';

    this.axisMax = this.data.axis_max_min || this.defaultAxisMax();
    this.baselineF = this.data.cold && this.data.cold.baseline_f != null ? this.data.cold.baseline_f : 70;
    this.adaptiveId = root.getAttribute('data-adaptive-mode') || 'adaptive';

    this.mode = this.data.modes[0].id;
    this.temp = this.baselineF;
    this.scale = 'brightness';
    this.lastWidth = 0;

    this.bind();
    this.buildSliderTicks();
    this.syncControls();
    this.observe();
    this.render();
  }

  RuntimeChart.prototype.peakLumens = function () {
    var max = 0;
    this.data.modes.forEach(function (m) {
      (m.curve || []).forEach(function (p) {
        if (p[1] > max) max = p[1];
      });
    });
    return max || 1;
  };

  RuntimeChart.prototype.defaultAxisMax = function () {
    var longest = 0;
    this.data.modes.forEach(function (m) {
      if (m.runtime_min > longest) longest = m.runtime_min;
    });
    return Math.min(longest, 240) || 180;
  };

  RuntimeChart.prototype.modeById = function (id) {
    for (var i = 0; i < this.data.modes.length; i++) {
      if (this.data.modes[i].id === id) return this.data.modes[i];
    }
    return this.data.modes[0];
  };

  RuntimeChart.prototype.referenceMode = function () {
    var preferred = this.data.reference_mode || 'high';
    if (preferred !== this.mode) {
      for (var i = 0; i < this.data.modes.length; i++) {
        if (this.data.modes[i].id === preferred) return this.data.modes[i];
      }
    }
    for (var j = 0; j < this.data.modes.length; j++) {
      if (this.data.modes[j].id !== this.mode) return this.data.modes[j];
    }
    return null;
  };

  /* Capacity factor at the selected temperature. Interpolates between the
   * measured points in cold.factors. Per-mode overrides win when present. */
  RuntimeChart.prototype.factor = function (mode) {
    var cold = this.data.cold;
    if (!cold || !cold.enabled) return 1;

    var table = (mode && mode.cold_factors) || cold.factors;
    if (!table) return 1;

    var points = Object.keys(table)
      .map(function (k) { return [parseFloat(k), table[k]]; })
      .filter(function (p) { return !isNaN(p[0]); })
      .sort(function (a, b) { return a[0] - b[0]; });

    if (!points.length) return 1;
    if (this.temp <= points[0][0]) return points[0][1];
    if (this.temp >= points[points.length - 1][0]) return points[points.length - 1][1];

    for (var i = 1; i < points.length; i++) {
      if (this.temp <= points[i][0]) {
        var a = points[i - 1];
        var b = points[i];
        var t = (this.temp - a[0]) / (b[0] - a[0]);
        return a[1] + (b[1] - a[1]) * t;
      }
    }
    return 1;
  };

  /* A curve value expressed as a 0-1 share of full perceived brightness. */
  RuntimeChart.prototype.brightnessOf = function (value) {
    if (this.units === 'pct') return Math.max(0, value) / 100;
    return Math.pow(Math.max(0, value) / this.maxLumens, this.exponent);
  };

  /* A curve value in lumens. Null when the data is percentages and no peak
   * output was supplied, in which case the lumens view is unavailable. */
  RuntimeChart.prototype.lumensOf = function (value) {
    if (this.units === 'lumens') return value;
    if (!this.hasLumens) return null;
    return this.maxLumens * Math.pow(Math.max(0, value) / 100, 1 / this.exponent);
  };

  RuntimeChart.prototype.fraction = function (value) {
    if (this.scale === 'brightness') return this.brightnessOf(value);
    var lm = this.lumensOf(value);
    return lm == null ? this.brightnessOf(value) : lm / this.maxLumens;
  };

  /* Scales a mode's curve by the capacity factor and clips it at the axis
   * maximum, interpolating the crossing point so long modes end cleanly. */
  RuntimeChart.prototype.scaledCurve = function (mode, factor) {
    var pts = (mode.curve || []).map(function (p) { return [p[0] * factor, p[1]]; });
    var out = [];
    var clipped = false;

    for (var i = 0; i < pts.length; i++) {
      if (pts[i][0] <= this.axisMax) {
        out.push(pts[i]);
        continue;
      }
      var prev = pts[i - 1];
      if (prev) {
        var t = (this.axisMax - prev[0]) / (pts[i][0] - prev[0]);
        out.push([this.axisMax, prev[1] + (pts[i][1] - prev[1]) * t]);
      }
      clipped = true;
      break;
    }

    return { points: out, clipped: clipped };
  };

  RuntimeChart.prototype.geometry = function () {
    var w = this.svg.clientWidth;
    var h = this.svg.clientHeight;
    var compact = w < 480;
    return {
      w: w,
      h: h,
      compact: compact,
      x0: compact ? 46 : 64,
      x1: w - 6,
      yTop: compact ? 10 : 14,
      yBase: h - (compact ? 22 : 26),
      tickSize: compact ? 10 : 11,
      axisSize: compact ? 9 : 10,
      stroke: compact ? 2 : 2.5
    };
  };

  RuntimeChart.prototype.render = function () {
    var g = this.geometry();
    if (!g.w || !g.h) return;

    var self = this;
    var mode = this.modeById(this.mode);
    var ref = this.referenceMode();
    var factor = this.factor(mode);

    this.svg.setAttribute('viewBox', '0 0 ' + g.w + ' ' + g.h);
    while (this.svg.firstChild) this.svg.removeChild(this.svg.firstChild);

    var x = function (min) { return g.x0 + (min / self.axisMax) * (g.x1 - g.x0); };
    var y = function (lm) { return g.yBase - self.fraction(lm) * (g.yBase - g.yTop); };
    var mid = (g.yTop + g.yBase) / 2;

    var showLumens = this.scale === 'lumens' && this.hasLumens;

    /* Grid before axes so the axis lines paint over the ends of the
     * gridlines rather than being notched by them. Levels are fractions of
     * plot height, so they hold whichever scale is selected: on the
     * brightness axis 0.75 is 75% perceived, on the lumens axis it is 75%
     * of full output. */
    var levels = [0.25, 0.5, 0.75];
    levels.forEach(function (level) {
      var gy = g.yBase - level * (g.yBase - g.yTop);
      self.svg.appendChild(el('line', {
        x1: g.x0, y1: gy, x2: g.x1, y2: gy,
        stroke: 'currentColor', 'stroke-width': 1,
        opacity: level === 0.5 ? 0.12 : 0.07
      }));
    });

    /* Hourly verticals. Labelled every hour with room, every two without. */
    var hourStep = 60;
    var labelStep = g.compact ? (this.axisMax > 240 ? 120 : 60) : 60;
    if (this.axisMax <= 120) {
      hourStep = 30;
      labelStep = 30;
    }
    for (var gt = hourStep; gt < this.axisMax; gt += hourStep) {
      self.svg.appendChild(el('line', {
        x1: x(gt), y1: g.yTop, x2: x(gt), y2: g.yBase,
        stroke: 'currentColor', 'stroke-width': 1, opacity: 0.07
      }));
    }

    this.svg.appendChild(el('line', {
      x1: g.x0 - 1, y1: g.yTop, x2: g.x0 - 1, y2: g.yBase,
      stroke: 'currentColor', 'stroke-width': 1, opacity: 0.3
    }));
    this.svg.appendChild(el('line', {
      x1: g.x0 - 1, y1: g.yBase, x2: g.x1, y2: g.yBase,
      stroke: 'currentColor', 'stroke-width': 1, opacity: 0.3
    }));

    var axisTitle = el('text', {
      x: 12, y: mid,
      fill: 'currentColor', opacity: 0.6,
      'font-size': g.axisSize,
      'text-anchor': 'middle',
      transform: 'rotate(-90 12 ' + mid + ')'
    }, showLumens ? 'LUMENS' : 'PERCEIVED BRIGHTNESS');
    this.svg.appendChild(axisTitle);

    var labelFor = function (level) {
      if (showLumens) return String(Math.round(self.maxLumens * level));
      return Math.round(level * 100) + '%';
    };
    [1, 0.75, 0.5, 0.25, 0].forEach(function (level) {
      var ly = g.yBase - level * (g.yBase - g.yTop);
      self.svg.appendChild(el('text', {
        x: g.x0 - 7, y: ly + (level === 1 ? 4 : (level === 0 ? 3 : 4)),
        'text-anchor': 'end',
        fill: 'currentColor', opacity: 0.6, 'font-size': g.tickSize
      }, level === 0 ? '0' : labelFor(level)));
    });

    for (var t = 0; t <= this.axisMax; t += labelStep) {
      var anchor = t === 0 ? 'start' : (t >= this.axisMax ? 'end' : 'middle');
      this.svg.appendChild(el('text', {
        x: x(t), y: g.yBase + (g.compact ? 15 : 18), 'text-anchor': anchor,
        fill: 'currentColor', opacity: 0.6, 'font-size': g.tickSize
      }, clockLabel(t)));
    }

    var path = function (points) {
      return points.map(function (p, i) {
        var px = i === 0 ? g.x0 : x(p[0]);
        return (i ? 'L' : 'M') + px.toFixed(1) + ',' + y(p[1]).toFixed(1);
      }).join(' ');
    };

    if (ref) {
      var refCurve = this.scaledCurve(ref, this.factor(ref));
      this.svg.appendChild(el('path', {
        d: path(refCurve.points), fill: 'none',
        stroke: 'currentColor', opacity: 0.4, 'stroke-width': 1.5
      }));
    }

    /* Baseline ghost of the selected mode, shown only when the slider has
     * moved off the temperature the curves were measured at. */
    if (factor !== 1) {
      var ghost = this.scaledCurve(mode, 1);
      this.svg.appendChild(el('path', {
        d: path(ghost.points), fill: 'none',
        stroke: 'var(--rtc-accent)', opacity: 0.35,
        'stroke-width': 1, 'stroke-dasharray': '3 3'
      }));
    }

    var live = this.scaledCurve(mode, factor);
    this.svg.appendChild(el('path', {
      d: path(live.points), fill: 'none',
      stroke: 'var(--rtc-accent)', 'stroke-width': g.stroke,
      'stroke-linejoin': 'round'
    }));

    var last = live.points[live.points.length - 1];
    if (last) {
      if (live.clipped) {
        var arrowY = y(last[1]);
        this.svg.appendChild(el('path', {
          d: 'M' + (g.x1 - 7) + ',' + (arrowY - 4) + ' l7,4 l-7,4 z',
          fill: 'var(--rtc-accent)'
        }));
        this.svg.appendChild(el('text', {
          x: g.x1 - 10, y: arrowY - 6, 'text-anchor': 'end',
          fill: 'var(--rtc-accent)', 'font-size': g.tickSize
        }, 'keeps going'));
      } else {
        this.svg.appendChild(el('circle', {
          cx: x(last[0]), cy: g.yBase, r: g.compact ? 3 : 4,
          fill: 'var(--rtc-accent)'
        }));
      }
    }

    this.updateStats(mode, ref, factor);
    this.lastWidth = g.w;
  };

  RuntimeChart.prototype.updateStats = function (mode, ref, factor) {
    var start = mode.curve && mode.curve.length ? mode.curve[0][1] : 0;
    var brightness = Math.round(this.brightnessOf(start) * 100);
    var lumens = this.lumensOf(start);

    this.setText('[data-rtc-runtime]', durationLabel(mode.runtime_min * factor));
    this.setText('[data-rtc-brightness]', brightness + '%');
    this.setText('[data-rtc-legend-live]', mode.name);
    this.setText('[data-rtc-legend-ref]', ref ? ref.name : '');
    this.setText('[data-rtc-temp]', this.temp + '\u00B0F');

    var lumensStat = this.root.querySelector('[data-rtc-lumens-stat]');
    if (lumens == null) {
      if (lumensStat) lumensStat.hidden = true;
    } else {
      if (lumensStat) lumensStat.hidden = false;
      this.setText('[data-rtc-lumens]', Math.round(lumens).toLocaleString());
    }

    var scaleBtn = this.root.querySelector('[data-rtc-scale]');
    if (scaleBtn) {
      scaleBtn.hidden = !this.hasLumens;
      scaleBtn.textContent = this.scale === 'brightness' ? 'Show lumens' : 'Show brightness';
    }

    var adaptiveTrigger = this.root.querySelector('[data-rtc-adaptive-trigger]');
    if (adaptiveTrigger) {
      var relevant = mode.id === this.adaptiveId;
      adaptiveTrigger.hidden = !relevant;
      if (!relevant) this.closePanel('adaptive');
    }
  };

  RuntimeChart.prototype.setText = function (selector, value) {
    var node = this.root.querySelector(selector);
    if (node) node.textContent = value;
  };

  RuntimeChart.prototype.buildSliderTicks = function () {
    var host = this.root.querySelector('[data-rtc-slider-ticks]');
    var slider = this.root.querySelector('[data-rtc-slider]');
    if (!host || !slider) return;

    var min = parseFloat(slider.min);
    var max = parseFloat(slider.max);
    var marks = (this.data.cold && this.data.cold.ticks_f) || [min, 0, 32];

    /* The thumb parks at the maximum by default. The tick row sits 12px below
     * the track so the label stays readable underneath it. */
    host.innerHTML = marks
      .filter(function (v) { return v >= min && v <= max; })
      .map(function (v) { return '<span>' + v + '\u00B0</span>'; })
      .join('');
  };

  RuntimeChart.prototype.syncControls = function () {
    var self = this;
    this.root.querySelectorAll('[data-rtc-mode]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-rtc-mode') === self.mode ? 'true' : 'false');
    });
  };

  /* Impulse's collapsible tab clips with overflow: hidden and manages
   * .collapsible-content's height in JS. Expanding a panel inside the tab
   * changes the content height after that was set, and measuring it
   * ourselves is unreliable (lazy images have no height yet). The tab is
   * auto-height by design, so the safe move is to hand every open ancestor
   * back to height: auto and let the browser size it. */
  RuntimeChart.prototype.refreshCollapsibles = function () {
    var node = this.root.parentElement;
    while (node && node !== document.body) {
      if (node.classList && node.classList.contains('collapsible-content') &&
          node.classList.contains('is-open')) {
        node.style.height = 'auto';
      }
      node = node.parentElement;
    }
  };

  RuntimeChart.prototype.openPanel = function (key, trigger) {
    var panel = this.root.querySelector('[data-rtc-panel="' + key + '"]');
    if (!panel) return;
    panel.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (key === 'cold') this.setColdIcon('\u2212');
    this.refreshCollapsibles();
  };

  RuntimeChart.prototype.closePanel = function (key) {
    var panel = this.root.querySelector('[data-rtc-panel="' + key + '"]');
    if (!panel) return;
    panel.hidden = true;
    var trigger = this.root.querySelector('[data-rtc-disclose="' + key + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (key === 'cold') this.setColdIcon('+');
    this.refreshCollapsibles();
  };

  RuntimeChart.prototype.setColdIcon = function (glyph) {
    var icon = this.root.querySelector('[data-rtc-cold-icon]');
    if (icon) icon.textContent = glyph;
  };

  RuntimeChart.prototype.bind = function () {
    var self = this;

    var pills = this.root.querySelector('[data-rtc-pills]');
    if (pills) {
      pills.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-rtc-mode]');
        if (!btn) return;
        self.mode = btn.getAttribute('data-rtc-mode');
        self.syncControls();
        self.render();
      });
    }

    var scaleBtn = this.root.querySelector('[data-rtc-scale]');
    if (scaleBtn) {
      scaleBtn.addEventListener('click', function () {
        self.scale = self.scale === 'brightness' ? 'lumens' : 'brightness';
        self.render();
      });
    }

    var slider = this.root.querySelector('[data-rtc-slider]');
    if (slider) {
      slider.addEventListener('input', function () {
        self.temp = parseInt(this.value, 10);
        self.render();
      });
    }

    this.root.querySelectorAll('[data-rtc-disclose]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var key = this.getAttribute('data-rtc-disclose');
        var panel = self.root.querySelector('[data-rtc-panel="' + key + '"]');
        if (!panel) return;
        if (panel.hidden) {
          self.openPanel(key, this);
        } else {
          self.closePanel(key);
        }
      });
    });

    /* Images inside panels can land after the panel opens. Re-release the
     * surrounding collapsible once they have real dimensions. */
    this.root.querySelectorAll('.rtc__panel img').forEach(function (img) {
      img.addEventListener('load', function () { self.refreshCollapsibles(); });
    });
  };

  RuntimeChart.prototype.observe = function () {
    var self = this;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', function () { self.render(); });
      return;
    }
    var pending;
    this.observer = new ResizeObserver(function () {
      window.clearTimeout(pending);
      pending = window.setTimeout(function () {
        var width = self.svg.clientWidth;
        if (!width || width === self.lastWidth) return;
        self.render();
      }, 100);
    });
    this.observer.observe(this.svg);
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-runtime-chart]').forEach(function (root) {
      if (root.dataset.rtcReady === 'true') return;
      root.dataset.rtcReady = 'true';
      new RuntimeChart(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.runtimeChartInit = init;
})();
